import NewColors from "./NewColors";
import { createTheme } from "@mui/material";

const active = NewColors.active;

/**
 * Darken/lighten a hex color by a percentage (-100 to 100).
 * Used to derive primary.dark / secondary.dark for hover states.
 */
function adjustColorBrightness(hex, percent) {
  if (!hex || !/^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/.test(hex)) return hex;
  const num = parseInt(hex.replace("#", ""), 16);
  const amt = Math.round(2.55 * percent);
  const R = (num >> 16) + amt;
  const G = ((num >> 8) & 0x00ff) + amt;
  const B = (num & 0x0000ff) + amt;
  return (
    "#" +
    (
      0x1000000 +
      (R < 255 ? (R < 1 ? 0 : R) : 255) * 0x10000 +
      (G < 255 ? (G < 1 ? 0 : G) : 255) * 0x100 +
      (B < 255 ? (B < 1 ? 0 : B) : 255)
    )
      .toString(16)
      .slice(1)
  );
}

function getTextColor(backgroundColor) {
  if (!backgroundColor || !/^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/.test(backgroundColor))
    return "#ffffff";
  const r = parseInt(backgroundColor.slice(1, 3), 16);
  const g = parseInt(backgroundColor.slice(3, 5), 16);
  const b = parseInt(backgroundColor.slice(5, 7), 16);
  const brightness = (r * 299 + g * 587 + b * 114) / 1000;
  return brightness > 140 ? "#000000" : "#ffffff";
}

/**
 * Build MUI theme from organization branding colors.
 * @param {Object | null} brandingColors - { primary, primaryLight, secondary, secondaryLight, primaryText?, primaryLightText?, secondaryText?, secondaryLightText? }
 * @returns {Object} MUI theme
 */
export function createBrandedTheme(brandingColors) {
  const hasBranding =
    brandingColors &&
    (brandingColors.primary || brandingColors.secondary);

  const primaryMain = hasBranding
    ? brandingColors.primary || active.primary
    : active.primary;
  const primaryLight = hasBranding
    ? brandingColors.primaryLight || adjustColorBrightness(primaryMain, 40)
    : NewColors.sectionBackground;
  const primaryDark = hasBranding
    ? adjustColorBrightness(primaryMain, -15)
    : active.primaryButtonHover || NewColors.triadicPower.buttons.primaryHover;
  const primaryContrastText =
    hasBranding && brandingColors.primaryText != null
      ? brandingColors.primaryText
      : getTextColor(primaryMain);

  const secondaryMain = hasBranding
    ? brandingColors.secondary || active.secondary
    : active.secondary;
  const secondaryLight = hasBranding
    ? brandingColors.secondaryLight || adjustColorBrightness(secondaryMain, 40)
    : NewColors.triadicPower.buttons.cta;
  const secondaryDark = hasBranding
    ? adjustColorBrightness(secondaryMain, -15)
    : NewColors.triadicPower.buttons.ctaHover;
  const secondaryContrastText =
    hasBranding && brandingColors.secondaryText != null
      ? brandingColors.secondaryText
      : getTextColor(secondaryMain);

  const primaryForTypography = hasBranding ? primaryMain : active.primary;
  const pageBg = "#F6F7FB";

  return createTheme({
    palette: {
      primary: {
        main: primaryMain,
        light: primaryLight,
        dark: primaryDark,
        contrastText: primaryContrastText,
      },
      secondary: {
        main: secondaryMain,
        light: secondaryLight,
        dark: secondaryDark,
        contrastText: secondaryContrastText,
      },
      background: {
        default: pageBg,
        paper: "#FFFFFF",
      },
      divider: "rgba(0, 0, 0, 0.08)",
      brand: {
        main: primaryMain,
        light: primaryLight,
        dark: primaryDark,
      },
      link: {
        main: primaryMain,
        dark: primaryDark,
      },
    },
    shape: {
      borderRadius: 12,
    },
    typography: {
      fontFamily: ["Lato", "sans-serif"].join(","),
      h3: { color: primaryForTypography, fontSize: 30, fontWeight: 700 },
      h4: { fontSize: 25, fontWeight: 700, color: primaryForTypography },
      h5: { fontWeight: 700, color: primaryForTypography },
      h6: { fontWeight: 700 },
      button: { textTransform: "none", fontWeight: 600 },
    },
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          body: {
            backgroundColor: pageBg,
          },
          a: {
            color: primaryMain,
            textDecorationColor: `${primaryMain}80`,
          },
        },
      },
      MuiAppBar: {
        styleOverrides: {
          root: {
            boxShadow: "none",
            borderBottom: "1px solid rgba(0,0,0,0.08)",
          },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            borderRadius: 16,
            boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
            border: "1px solid rgba(0,0,0,0.08)",
            backgroundImage: "none",
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundImage: "none",
          },
        },
      },
      MuiButton: {
        styleOverrides: {
          root: { textTransform: "none", fontWeight: 600, borderRadius: 20 },
        },
      },
      MuiDialog: {
        styleOverrides: {
          paper: { borderRadius: 10 },
        },
      },
    },
  });
}

const mainTheme = createBrandedTheme(null);
export default mainTheme;
